import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Dumbbell, Calendar, Flame } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WeightProgressionChart } from "@/components/weight-progression-chart";
import { WorkoutCalendar } from "@/components/workout-calendar";
import { trpc } from "@/utils/trpc";

export const Route = createFileRoute("/_protected/")({
  component: HomeComponent,
});

function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function HomeComponent() {
  const sessionsQuery = useQuery(
    trpc.sessions.list.queryOptions({ limit: 20 })
  );
  const analyticsQuery = useQuery(
    trpc.analytics.getStats.queryOptions({ days: 35 })
  );

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Dumbbell className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {analyticsQuery.isLoading ? (
                    <span className="inline-block h-7 w-8 bg-muted animate-pulse rounded" />
                  ) : (
                    analyticsQuery.data?.workoutsThisWeek ?? 0
                  )}
                </p>
                <p className="text-sm text-muted-foreground">This week</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {analyticsQuery.isLoading ? (
                    <span className="inline-block h-7 w-8 bg-muted animate-pulse rounded" />
                  ) : (
                    analyticsQuery.data?.totalWorkouts ?? 0
                  )}
                </p>
                <p className="text-sm text-muted-foreground">Last 30 days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Flame className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                {analyticsQuery.isLoading ? (
                  <p className="text-2xl font-bold">
                    <span className="inline-block h-7 w-8 bg-muted animate-pulse rounded" />
                  </p>
                ) : (analyticsQuery.data?.streak ?? 0) > 0 ? (
                  <>
                    <p className="text-2xl font-bold">{analyticsQuery.data?.streak}</p>
                    <p className="text-sm text-muted-foreground">Week streak</p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Start your streak!</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weight Progression Chart */}
      <WeightProgressionChart />

      {/* Workout Calendar */}
      <WorkoutCalendar
        workoutDates={analyticsQuery.data?.workoutDates ?? []}
        isLoading={analyticsQuery.isLoading}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Recent Workouts</h1>
        <Link to="/history" className="text-sm text-muted-foreground hover:text-foreground">
          View All History
        </Link>
      </div>

      {sessionsQuery.isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-5 w-32 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-4 w-48 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {sessionsQuery.error && (
        <Card>
          <CardContent className="py-6">
            <p className="text-destructive">
              Failed to load sessions: {sessionsQuery.error.message}
            </p>
          </CardContent>
        </Card>
      )}

      {sessionsQuery.data && sessionsQuery.data.length === 0 && (
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground">No workouts recorded yet.</p>
          </CardContent>
        </Card>
      )}

      {sessionsQuery.data && sessionsQuery.data.length > 0 && (
        <div className="space-y-3">
          {sessionsQuery.data.map((session) => (
            <Link
              key={session.id}
              to="/session/$sessionId"
              params={{ sessionId: session.id }}
            >
              <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{session.template.name}</span>
                    {!session.completedAt && (
                      <span className="text-xs font-normal bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 px-2 py-0.5 rounded">
                        In Progress
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{formatDate(session.startedAt)}</span>
                    <span>{formatTime(session.startedAt)}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
